import React from 'react';
import { DndSystem } from '../../dnd-system/core/DndSystem';
import type {
	DropZoneContext,
	DropZoneStrategy,
	DropZoneConfig,
	DragData,
	DropPosition,
	DropResult
} from '../../dnd-system/types';
import { App } from "../../app/AppSingleton";
import { AppSignals } from '../../app/AppSignals';
import './ColumnDropzone.css';
import {ViewModeEnum} from "../../models/view-types";
import {Board, EntityID} from "../../models/types";

interface ColumnDropzoneProps {
	index: number;
	isFirst?: boolean;
	isLast?: boolean;
	viewMode?: 'columns' | 'list';

	board:Board
}

interface ColumnDropzoneState {
	isOver: boolean;
}

class ColumnDropzone extends React.Component<ColumnDropzoneProps, ColumnDropzoneState> {
	private droppableId: string;
	private dropzoneRef: React.RefObject<HTMLDivElement>;
	private dndSystem = DndSystem.getInstance();
	private isOverRef: { current: boolean };
	private unregisterFn: (() => void) | null = null;

	// Проверяем, находится ли доска в режиме редактирования
	isBoardEditing = (): boolean => {
		return this.getBoard().isEditing || false;
	}

	getPositionClass = (): string => {
		const { isFirst, isLast } = this.props;
		const viewMode = App.getInstance().getViewMode();
		const baseClass = `column-dropzone column-dropzone--view_${viewMode}`;

		if (isFirst) {
			return `${baseClass} column-dropzone--position_first`;
		}
		if (isLast) {
			return `${baseClass} column-dropzone--position_middle`; // column-dropzone--position_last
		}
		return `${baseClass} column-dropzone--position_middle`;
	}

	setIsOver = (value: boolean): void => {
		this.setState({ isOver: value });
		this.isOverRef.current = value;
	}

	constructor(props: ColumnDropzoneProps) {
		super(props);

		this.droppableId = `column-dropzone-${props.index}`;
		this.dropzoneRef = React.createRef<HTMLDivElement>();
		this.isOverRef = { current: false };

		this.state = {
			isOver: false
		};
	}

	// Метод для вычисления позиции дропа
	calculateDropPosition = (
		x: number, y: number, context: DropZoneContext, dragData: DragData): DropPosition | null => {
		const { index, isFirst = false, isLast = false, viewMode = ViewModeEnum.List } = this.props;

		// Проверяем, что перетаскивается именно колонка
		if (dragData.type !== 'column') {
			return null;
		}

		const element = context.element;
		const rect = element.getBoundingClientRect();

		// Проверяем попадание в dropzone
		if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
			return null;
		}

		// Определяем тип дропзоны с учетом режима просмотра
		const positionType = isFirst ? 'first-column' :
			isLast ? 'last-column' :
				'between-columns';

		// Настраиваем визуальные стили в зависимости от режима
		let visualStyle;
		if (viewMode === ViewModeEnum.List) {
			// Вертикальный индикатор для режима списка
			visualStyle = {
				top: `${rect.top}px`,
				left: `${rect.left}px`,
				width: `${rect.width}px`,
				height: `${rect.height}px`,
				backgroundColor: 'rgba(77, 171, 247, 0.1)',
				border: '2px dashed #4dabf7',
				borderRadius: '4px',
				pointerEvents: 'none'
			};
		} else {
			// Горизонтальный индикатор для режима колонок
			visualStyle = {
				top: `${rect.top}px`,
				left: `${rect.left}px`,
				width: `${rect.width}px`,
				height: `${rect.height}px`,
				backgroundColor: 'rgba(77, 171, 247, 0.1)',
				border: '2px dashed #4dabf7',
				borderRadius: '8px',
				pointerEvents: 'none'
			};
		}

		return {
			type: positionType,
			relativeX: 0.5,
			relativeY: 0.5,
			data: {
				dropzoneIndex: index,
				position: isFirst ? 'before-first' :
					isLast ? 'after-last' :
						'between',
				sourceColumnId: dragData.data?.columnId,
				viewMode: viewMode
			},
			visual: {
				className: `column-dropzone-indicator--${positionType} column-dropzone-indicator--view_${viewMode}`,
				style: visualStyle
			}
		};
	}

	// Метод для обработки входа перетаскивания
	handleDragEnter = (context: DropZoneContext, dragData: DragData, position: DropPosition): void => {
		const { index } = this.props;

		this.setIsOver(true);
		context.element.classList.add('column-dropzone--drag-over');

		// Уведомляем о начале перетаскивания в дропзону
		AppSignals.columnDragOver.emit({
			dropzoneIndex: index,
			sourceColumnId: dragData.data?.columnId,
			position: position.data?.position
		});
	}

	// Метод для обработки выхода перетаскивания
	handleDragLeave = (context: DropZoneContext, dragData: DragData): void => {
		const { index } = this.props;

		this.setIsOver(false);
		context.element.classList.remove('column-dropzone--drag-over');

		// Уведомляем о выходе из дропзоны
		AppSignals.columnDragLeave.emit({
			dropzoneIndex: index,
			sourceColumnId: dragData.data?.columnId
		});
	}

	// Метод для обработки дропа
	handleDrop = (context: DropZoneContext, dragData: DragData, position: DropPosition): DropResult => {
		const { index, isFirst = false, isLast = false } = this.props;

		this.setIsOver(false);
		context.element.classList.remove('column-dropzone--drag-over');

		try {
			const app = App.getInstance();
			const sourceColumnId = dragData.data?.columnId;

			if (!sourceColumnId) {
				throw new Error('Неизвестная колонка для перемещения');
			}

			// Определяем новую позицию для колонки
			let targetIndex = index;

			if (isFirst) {
				// Вставка перед первой колонкой
				targetIndex = 0;
			} else if (isLast) {
				// Вставка после последней колонки
				targetIndex = this.getBoard().columns.length;
			}

			// Находим исходный индекс колонки
			const columns = this.getBoard().columns;
			const sourceColumn = columns.find(col => col.id === sourceColumnId);
			const sourceIndex = sourceColumn ? columns.indexOf(sourceColumn) : -1;

			if (sourceIndex === -1) {
				throw new Error(`Колонка с ID ${sourceColumnId} не найдена`);
			}

			// Если дропзона находится между колонками, и мы перетаскиваем колонку из того же набора,
			// нужно скорректировать индекс
			if (sourceIndex >= 0 && targetIndex > sourceIndex) {
				// Если перемещаем вправо, уменьшаем targetIndex на 1
				// потому что исходный элемент будет удален
				targetIndex--;
			}

			// Вызываем метод App для перемещения колонки с индексами
			if (app.columnMove) {
				app.columnMove(sourceIndex, targetIndex)
					.then(() => {
						AppSignals.columnMoved.emit({
							columnId: sourceColumnId,
							newIndex: targetIndex,
							oldIndex: sourceIndex
						});
					})
					.catch(console.error);
			} else {
				// Fallback для обратной совместимости
				console.warn('app.moveColumn не реализован, используем прямой вызов');
			}

			return {
				success: true,
				data: {
					columnId: sourceColumnId,
					newIndex: targetIndex,
					dropzoneIndex: index
				},
				message: 'Колонка успешно перемещена'
			};
		} catch (error) {
			console.error('Drop column failed:', error);
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Unknown error',
				revert: true
			};
		}
	}

	private getBoard():Board {
		return this.props.board;
	}

	componentDidMount() {
		if (!this.dropzoneRef.current || !this.isBoardEditing()) return;

		const { index, isFirst = false, isLast = false, viewMode = ViewModeEnum.List } = this.props;

		const context: DropZoneContext = {
			element: this.dropzoneRef.current,
			id: this.droppableId,
			acceptTypes: ['column'] // Только колонки можно перетаскивать в дропзону
		};

		const strategy: DropZoneStrategy = {
			cssClasses: {
				active: 'column-dropzone__line--active'
			},
			calculateDropPosition: this.calculateDropPosition,
			onDragEnter: this.handleDragEnter,
			onDragLeave: this.handleDragLeave,
			onDrop: this.handleDrop
		};

		const dropZoneConfig: DropZoneConfig = {
			context,
			strategy
		};

		this.unregisterFn = this.dndSystem.registerDropZone(dropZoneConfig);
	}

	componentWillUnmount() {
		if (this.unregisterFn) {
			this.unregisterFn();
		}

		if (this.isOverRef.current) {
			// Если компонент размонтируется во время перетаскивания
			const { index } = this.props;
			AppSignals.columnDragLeave.emit({
				dropzoneIndex: index,
				sourceColumnId: undefined,
			});
		}
	}

	render() {
		const { index, isFirst = false, isLast = false, viewMode = ViewModeEnum.List } = this.props;

		// Не показываем дропзону, если доска не в режиме редактирования
		const isEditing = this.isBoardEditing();
		if (!isEditing) {
			return null;
		}

		const { isOver } = this.state;

		return (
			<div
				ref={this.dropzoneRef}
				className={`column-dropzone ${this.getPositionClass()} ${isOver ? 'column-dropzone--drag-over' : ''}`}
				data-dropzone-index={index}
				aria-label={`Позиция для вставки колонки ${isFirst ? 'перед первой' : isLast ? 'после последней' : 'между колонками'}`}
			>
				<div
					className={`column-dropzone__line ${isOver ? 'column-dropzone__line--active' : ''}`}
				>
					{/* Визуальный индикатор */}
					{isOver && <div className="column-dropzone__indicator"></div>}
				</div>

				{/* Иконка дропзоны (появляется при наведении) */}
				<div
					className={`column-dropzone__icon-container ${isOver ? 'column-dropzone__icon-container--active' : ''}`}
				>
					<div className="column-dropzone__icon">
						<span>+</span>
					</div>
				</div>
			</div>
		);
	}
}

export default ColumnDropzone;